import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useGoogleLogin } from '@react-oauth/google'
import {
  ArrowLeftIcon,
} from '@heroicons/react/24/outline'
import { validatePan, validateAge18Plus, validatePhone } from '../../utils/validation'
import OnboardingLayout from '../../components/common/OnboardingLayout'
import { login, googleLogin, updateUser } from '../../store/slices/authSlice'
import { authApi, userApi } from '../../services/api'
import { resolvePostAuthRoute } from '../../utils/accessFlow'
import { RootState } from '../../store/store'

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Signup/login flow now ends at dashboard. KYC/eSign are only required
// when the user attempts to purchase a plan.
type Stage = 'email' | 'profile' | 'otp' | 'google-profile'

function OtpInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
      value={value}
      onChange={e => onChange(e.target.value.replace(/\D/g, ''))}
      placeholder="• • • • • •"
      autoFocus
      className="w-full bg-dark-800 border border-dark-600 text-gray-200 rounded-xl px-4 py-3 text-2xl font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
    />
  )
}

function GoogleButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      type="button" onClick={onClick} disabled={loading}
      className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-dark-600 rounded-xl text-sm font-medium text-gray-200 bg-dark-800 hover:bg-dark-700 transition-colors disabled:opacity-50"
    >
      <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Continue with Google
    </button>
  )
}

// ─── Main AuthPage ─────────────────────────────────────────────────────────────

export default function AuthPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { isAuthenticated, user: authenticatedUser } = useSelector((state: RootState) => state.auth)

  const [stage, setStage] = useState<Stage>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  // Whether OTP was sent for a returning user (has_active_plan known at check-email)
  const [returningHasPlan, setReturningHasPlan] = useState<boolean | null>(null)

  const [profile, setProfile] = useState({
    full_name: '', phone: '', pan_number: '', date_of_birth: '',
  })
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({})

  type ProfileForm = typeof profile

  // Google profile completion state. The JWT is deliberately retained while
  // this form is shown; submitting it must not trigger OAuth again.
  const hasGoogleClientId = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID)

  useEffect(() => {
    // Pre-fill email from query param (e.g. ?email=user@example.com)
    const e = params.get('email')
    if (e) setEmail(e)
  }, [params])

  useEffect(() => {
    if (!isAuthenticated || !authenticatedUser) return

    const profileIsIncomplete = !authenticatedUser.phone
      || !authenticatedUser.pan_number
      || !authenticatedUser.date_of_birth
    if (!profileIsIncomplete) return

    setProfile({
      full_name: authenticatedUser.full_name || '',
      phone: authenticatedUser.phone || '',
      pan_number: authenticatedUser.pan_number || '',
      date_of_birth: authenticatedUser.date_of_birth?.split('T')[0] || '',
    })
    setStage('google-profile')
  }, [isAuthenticated, authenticatedUser])

  const afterLogin = (payload: any) => {
    navigate(resolvePostAuthRoute(payload))
  }

  // ── Email submit ─────────────────────────────────────────────────────────────
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return
    setLoading(true)
    setError('')
    try {
      const res = await authApi.checkEmail(email)
      if (res.exists) {
        setReturningHasPlan(res.has_active_plan ?? false)
        setStage('otp')
      } else {
        setStage('profile')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // ── Profile submit (new user) ─────────────────────────────────────────────────
  const normalizeProfileValue = (field: keyof ProfileForm, rawValue: string) => {
    if (field === 'phone') return rawValue.replace(/\D/g, '').slice(0, 10)
    if (field === 'pan_number') return rawValue.toUpperCase()
    return rawValue
  }

  const validateProfileField = (field: keyof ProfileForm, value: string) => {
    if (field === 'full_name') {
      if (!value.trim()) return 'Full name is required.'
      return ''
    }
    if (field === 'phone') {
      if (!value.trim()) return 'Please enter a valid 10-digit mobile number.'
      if (!validatePhone(value)) return 'Please enter a valid 10-digit mobile number.'
      return ''
    }
    if (field === 'pan_number') {
      if (!value.trim() || !validatePan(value)) return 'Enter a valid 10-character PAN (e.g. ABCDE1234F)'
      return ''
    }
    if (field === 'date_of_birth') {
      if (!value) return 'Date of birth is required'
      if (!validateAge18Plus(value)) return 'You must be at least 18 years old'
      return ''
    }
    return ''
  }

  const validateProfile = (values: ProfileForm = profile) => {
    const nextErrors: Record<string, string> = {}
    ;(['full_name', 'phone', 'pan_number', 'date_of_birth'] as (keyof ProfileForm)[]).forEach((field) => {
      const fieldError = validateProfileField(field, values[field])
      if (fieldError) nextErrors[field] = fieldError
    })
    return nextErrors
  }

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs = validateProfile()
    if (Object.keys(errs).length) { setProfileErrors(errs); return }
    setLoading(true)
    setError('')
    try {
      await authApi.register({
        email,
        full_name: profile.full_name,
        phone: profile.phone,
        pan_number: profile.pan_number.toUpperCase(),
        date_of_birth: profile.date_of_birth,
      })
      setReturningHasPlan(false)
      setStage('otp')
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed.')
    } finally {
      setLoading(false)
    }
  }

  // ── Google profile submit (missing phone/PAN after Google login) ──────────────
  const handleGoogleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errs = validateProfile()
    if (Object.keys(errs).length) { setProfileErrors(errs); return }
    setLoading(true)
    setError('')
    try {
      // First update the profile in the backend with the new phone/PAN/DOB
      const updatedUser = await userApi.updateProfile({
        full_name: profile.full_name,
        phone: profile.phone,
        pan_number: profile.pan_number.toUpperCase(),
        date_of_birth: profile.date_of_birth,
      })
      dispatch(updateUser(updatedUser))
      afterLogin({ user: updatedUser, has_active_plan: false })
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update profile.')
    } finally {
      setLoading(false)
    }
  }

  // ── OTP verify ──────────────────────────────────────────────────────────────
  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (otp.length < 6) return
    setLoading(true)
    setError('')
    const result = await dispatch(login({ email, otp_code: otp }) as any)
    setLoading(false)
    if (login.fulfilled.match(result)) {
      afterLogin(result.payload)
    } else {
      const errorMsg = result.payload as string || 'Invalid OTP. Please try again.'
      setError(errorMsg)
      if (errorMsg.toLowerCase().includes('expired')) {
        setStage('email')
        setOtp('')
      }
    }
  }

  const handleResend = async () => {
    setResending(true)
    setResent(false)
    setError('')
    try {
      await authApi.sendOtp(email)
      setResent(true)
      setOtp('')
    } catch {
      setError('Failed to resend OTP.')
    } finally {
      setResending(false)
    }
  }

  // ── Google login ─────────────────────────────────────────────────────────────
  const handleGoogleResponse = async (credentialOrToken: string) => {
    console.log('[Auth] Google login response received, starting backend verification...')
    setLoading(true)
    setError('')
    const result = await dispatch(
      googleLogin({
        code: credentialOrToken,
        redirect_uri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || 'postmessage',
      }) as any
    )
    setLoading(false)
    if (googleLogin.fulfilled.match(result)) {
      console.log('[Auth] Google login successful, processing user data...')
      const payload = result.payload
      const user = payload?.user
      if (user?.is_admin) { navigate('/admin'); return }
      if (payload?.needs_profile) {
        // The access token is already stored by googleLogin. Keep this screen
        // mounted and complete the profile with that same authenticated session.
        setProfile({
          full_name: user?.full_name || '',
          phone: user?.phone || '',
          pan_number: user?.pan_number || '',
          date_of_birth: user?.date_of_birth?.split('T')[0] || '',
        })
        setStage('google-profile')
        return
      }
      afterLogin(payload)
    } else {
      console.error('[Auth] Backend verification of Google token failed:', result.payload)
      setError(result.payload as string || 'Google login failed.')
    }
  }

  const signInWithGoogle = useGoogleLogin({
    onSuccess: r => {
      console.log('[Auth] Google OAuth popup success, obtained code.')
      handleGoogleResponse(r.code)
    },
    onError: () => {
      console.warn('[Auth] Google sign-in was cancelled before completion.')
      setError('Google sign-in was cancelled before completion.')
    },
    onNonOAuthError: (err) => {
      console.error('[Auth] Google OAuth non-OAuth error:', err)
      if (err.type === 'popup_closed') {
        setError('Google sign-in was cancelled before completion.')
      } else if (err.type === 'popup_failed_to_open') {
        setError('Unable to open Google sign-in popup. Please allow popups and try again.')
      } else {
        setError('Google sign-in failed due to a network or browser error. Please try again.')
      }
    },
    flow: 'auth-code',
    scope: 'openid email profile',
  })

  // ── Shared field helpers ─────────────────────────────────────────────────────
  const inp = "w-full bg-dark-800 border border-dark-600 text-gray-200 placeholder-gray-500 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
  const fieldErr = (key: string) => profileErrors[key]
    ? <p className="text-xs text-red-400 mt-1">{profileErrors[key]}</p> : null

  const setP = (k: keyof ProfileForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = normalizeProfileValue(k, e.target.value)
    setProfile(p => ({ ...p, [k]: value }))
    setProfileErrors(prev => {
      const next = { ...prev }
      const fieldError = validateProfileField(k, value)
      if (fieldError) next[k] = fieldError
      else delete next[k]
      return next
    })
  }

  const handleBlur = (k: keyof ProfileForm) => (e: React.FocusEvent<HTMLInputElement>) => {
    const value = normalizeProfileValue(k, e.target.value)
    setProfileErrors(prev => {
      const next = { ...prev }
      const fieldError = validateProfileField(k, value)
      if (fieldError) next[k] = fieldError
      else delete next[k]
      return next
    })
  }

  // ── Layout ───────────────────────────────────────────────────────────────────
  const stageLabel: Record<Stage, string> = {
    email: 'Sign in or create account',
    profile: 'Complete your profile',
    otp: 'Verify your email',
    'google-profile': 'Complete your profile',
  }

  return (
    <OnboardingLayout
      currentStep="account"
      showSteps={false}
      title="Sign in or create account"
      subtitle={stageLabel[stage]}
      footerText="Secure sign-in with OTP and Google OAuth."
    >
      {error && (
        <div className="mb-4 bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm">{error}</div>
      )}

      {/* ── Email stage ── */}
      {stage === 'email' && (
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Email address</label>
            <input type="email" value={email} onChange={e => { setEmail(e.target.value); setError('') }}
              className={inp} placeholder="you@example.com" required autoFocus />
          </div>
          <button type="submit" disabled={loading || !email}
            className="w-full py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
            {loading ? 'Checking…' : 'Continue'}
          </button>
          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-dark-600" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-2 bg-dark-800 text-gray-500">or</span></div>
          </div>
          <GoogleButton
            onClick={() => {
              if (!hasGoogleClientId) {
                setError('Google login is not configured. Please contact support.')
                return
              }
              signInWithGoogle()
            }}
            loading={loading || !hasGoogleClientId}
          />
          <p className="text-center text-xs text-gray-500">
            New here? Just enter your email - we'll walk you through setup.
          </p>
          <p className="text-center text-xs text-gray-600">
            By continuing you agree to our{' '}
            <Link to="/terms" className="text-gray-500 hover:text-gray-400">Terms</Link> &amp;{' '}
            <Link to="/privacy" className="text-gray-500 hover:text-gray-400">Privacy Policy</Link>
          </p>
        </form>
      )}

      {/* ── Profile stage (new user) ── */}
      {(stage === 'profile' || stage === 'google-profile') && (
        <form onSubmit={stage === 'google-profile' ? handleGoogleProfileSubmit : handleProfileSubmit} className="space-y-4">
          <p className="text-sm text-gray-400 mb-2">
            {stage === 'google-profile'
              ? "We need a few more details to set up your account."
              : `No account found for ${email}. Fill in your details to create one.`}
          </p>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Full Name *</label>
            <input type="text" value={profile.full_name} onChange={setP('full_name')} onBlur={handleBlur('full_name')} className={inp} placeholder="Your Full Name" />
            {fieldErr('full_name')}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Mobile Number *</label>
            <input type="tel" value={profile.phone} onChange={setP('phone')} onBlur={handleBlur('phone')} className={inp} placeholder="9876543210" maxLength={10} />
            {fieldErr('phone')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">PAN Number *</label>
              <input type="text" value={profile.pan_number}
                onChange={setP('pan_number')}
                onBlur={handleBlur('pan_number')}
                className={`${inp} font-mono tracking-wider`} placeholder="ABCDE1234F" maxLength={10} />
              {fieldErr('pan_number')}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Date of Birth *</label>
              <input type="date" value={profile.date_of_birth} onChange={setP('date_of_birth')} onBlur={handleBlur('date_of_birth')} className={inp} max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]} />
              {fieldErr('date_of_birth')}
            </div>
          </div>
          <button type="submit" disabled={loading}
            className="w-full py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
            {loading ? 'Saving…' : stage === 'google-profile' ? 'Continue to dashboard' : 'Send OTP to verify email'}
          </button>
          {stage !== 'google-profile' && (
            <button type="button" onClick={() => { setStage('email'); setError('') }}
              className="w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors">
              <ArrowLeftIcon className="w-3 h-3" /> Use a different email
            </button>
          )}
        </form>
      )}

      {/* ── OTP stage ── */}
      {stage === 'otp' && (
        <form onSubmit={handleOtpSubmit} className="space-y-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <p className="text-sm text-gray-300">We sent a 6-digit code to</p>
            <p className="font-semibold text-white">{email}</p>
            {resent && <p className="text-xs text-green-400 mt-1">New OTP sent.</p>}
          </div>
          <OtpInput value={otp} onChange={v => { setOtp(v); setError('') }} />
          <button type="submit" disabled={loading || otp.length < 6}
            className="w-full py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
            {loading ? 'Verifying…' : returningHasPlan ? 'Sign In' : 'Verify & Continue'}
          </button>
          <div className="flex justify-between text-sm">
            <button type="button" onClick={() => { setStage('email'); setOtp(''); setError('') }}
              className="text-gray-500 hover:text-gray-300 flex items-center gap-1 text-xs">
              <ArrowLeftIcon className="w-3 h-3" /> Change email
            </button>
            <button type="button" onClick={handleResend} disabled={resending}
              className="text-primary-400 hover:text-primary-300 text-xs disabled:opacity-50">
              {resending ? 'Sending…' : 'Resend OTP'}
            </button>
          </div>
        </form>
      )}
    </OnboardingLayout>
  )
}
